import { useLayoutEffect, useState } from 'react';
import { headerActionsFitInTitleRow, intrinsicFlexContentWidth } from '../utils/header-actions-fit';

interface UseHeaderActionsFitArgs {
  enabled: boolean;
  row: HTMLElement | null;
  title: HTMLElement | null;
  actions: HTMLElement | null;
  chrome: HTMLElement | null;
}

function readGap(element: HTMLElement | null): number {
  if (!element) {
    return 0;
  }
  const styles = getComputedStyle(element);
  return Number.parseFloat(styles.columnGap || styles.gap) || 0;
}

export function useHeaderActionsFit({
  enabled,
  row,
  title,
  actions,
  chrome,
}: UseHeaderActionsFitArgs): boolean {
  const [fits, setFits] = useState(true);

  useLayoutEffect(() => {
    if (!enabled || !row || !title) {
      setFits(true);
      return;
    }

    const measure = (): void => {
      const rowStyles = getComputedStyle(row);
      const paddingX =
        (Number.parseFloat(rowStyles.paddingLeft) || 0) + (Number.parseFloat(rowStyles.paddingRight) || 0);
      const actionsWidth =
        actions && actions.childElementCount > 0 ? intrinsicFlexContentWidth(actions) : 0;
      const chromeWidth =
        chrome && chrome.childElementCount > 0 ? chrome.getBoundingClientRect().width : 0;

      setFits(
        headerActionsFitInTitleRow({
          availableWidth: row.clientWidth - paddingX,
          titleWidth: intrinsicFlexContentWidth(title),
          actionsWidth,
          chromeWidth,
          rowGap: readGap(row),
          chromeGap: readGap(chrome?.parentElement ?? null),
        }),
      );
    };

    measure();

    const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    resizeObserver?.observe(row);
    resizeObserver?.observe(title);
    if (actions) {
      resizeObserver?.observe(actions);
    }
    if (chrome) {
      resizeObserver?.observe(chrome);
    }

    const mutationObserver = new MutationObserver(measure);
    mutationObserver.observe(title, { childList: true, subtree: true, characterData: true });
    if (actions) {
      mutationObserver.observe(actions, { childList: true, subtree: true });
    }

    return () => {
      resizeObserver?.disconnect();
      mutationObserver.disconnect();
    };
  }, [enabled, row, title, actions, chrome]);

  return enabled ? fits : true;
}
